import * as React from 'react'
import { Toolbar, ToolbarButton } from '@radix-ui/react-toolbar'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { AttachmentIcon } from '~/components/icons/attachment-icon'
import { isReallyEmptyJson } from '@basehub/tiptap-api-utils'
import { Cross1Icon, PaperPlaneIcon } from '@radix-ui/react-icons'
import { startImageUpload } from '~/lib/tiptap/plugins/upload-image'
import { startVideoUpload } from '~/lib/tiptap/plugins/upload-video'
import { toast } from 'sonner'
import { Editor, useCurrentEditor } from '@tiptap/react'
import { ArchiveIcon } from '~/components/icons/archive'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useCommentDraftStorage } from '../use-comment-draft-storage'
import { getId } from '@basehub/utils/id/index'
import { SUPPORTED_LLM_FILE_TYPES } from '@basehub/shared-constants'
import { startFileUpload } from '~/lib/tiptap/plugins/upload-file'

export const CommentFormFooter = ({
  handleSubmit,
  handleCancel,
  handleArchive,
  submitIcon,
  commentIsEmpty,
  editorKey,
  selectionSlot,
  submitClassName,
}: {
  handleSubmit: (state: string, id?: string) => Promise<void>
  handleCancel?: () => void
  handleArchive?: () => void
  submitIcon?: React.ReactNode
  submitClassName?: string
  commentIsEmpty: boolean | undefined
  editorKey: string | undefined
  selectionSlot?: React.ReactNode
}) => {
  const { editor } = useCurrentEditor()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const { data } = useRepoReplicache()
  const teamId = data?.team.id
  const repoId = data?.repo.id
  const { draft, setDraft } = useCommentDraftStorage(editorKey)

  const isEmpty = useIsEditorEmpty(editor)

  return (
    <Toolbar className="flex justify-between text-dark-muted">
      <div className="flex gap-2">
        {selectionSlot}
        <ToolbarButton asChild tabIndex={0}>
          <IconButton asChild type="button">
            <label
              htmlFor="comment-file-input"
              className="font-medium text-dark-faint text-xs hover:text-dark-muted gap-1 !pl-0"
            >
              <AttachmentIcon className="size-3.5" />
              <span>Add file</span>
            </label>
          </IconButton>
        </ToolbarButton>
      </div>
      <div className="flex gap-2">
        {handleArchive && (
          <ToolbarButton type="button" asChild>
            <IconButton
              tabIndex={0}
              type="button"
              className="font-medium text-dark-faint text-xs hover:text-dark-muted gap-1 "
              onClick={(e) => {
                e.stopPropagation()
                if (!editor) return

                const json = editor.getJSON()
                if (!isReallyEmptyJson(json)) {
                  const id = draft.id
                  handleSubmit(JSON.stringify(json), id).then(() => {
                    setDraft({ id: getId(), value: '' })
                  })
                }
                handleArchive()
                return editor?.commands.clearContent()
              }}
            >
              <ArchiveIcon className="size-3.5" />
              <span>Archive {commentIsEmpty ? 'branch' : 'with comment'}</span>
            </IconButton>
          </ToolbarButton>
        )}
        {handleCancel && (
          <ToolbarButton type="button" asChild className="hover:text-dark-dim">
            <IconButton
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
                setDraft({ id: getId(), value: '' })
                return editor?.commands.clearContent()
              }}
              type="button"
              className="text-dark-faint"
            >
              <Cross1Icon />
            </IconButton>
          </ToolbarButton>
        )}
        <ToolbarButton asChild>
          <IconButton
            tabIndex={0}
            disabled={!!commentIsEmpty || isEmpty}
            onClick={(e) => {
              e.stopPropagation()
              if (!editor) return console.error('editor not found')

              const json = editor.getJSON()
              if (isEmpty) return
              const id = draft.id
              handleSubmit(JSON.stringify(json), id).then(() => {
                setDraft({ id: getId(), value: '' })
                editor.commands.clearContent()
                editor.commands.focus('end')
              })
            }}
            intent="primary"
            data-comment-submit-button
            className={submitClassName}
          >
            {submitIcon !== undefined ? (
              submitIcon
            ) : (
              <PaperPlaneIcon className="size-3.5" />
            )}
          </IconButton>
        </ToolbarButton>
      </div>
      {/* image or video */}
      <input
        ref={inputRef}
        type="file"
        accept={`image/*,video/*,${SUPPORTED_LLM_FILE_TYPES.join(',')}`}
        className="hidden"
        id="comment-file-input"
        onChange={(e) => {
          const file = e.target.files?.[0]
          const view = editor?.view
          if (!file) throw new Error('No file selected')
          if (!view || !editor?.$pos || !teamId || !repoId) {
            return
          }
          if (file.type.includes('video/')) {
            startVideoUpload(
              file,
              view,
              editor.state.selection.from,
              { teamId, repoId },
              { width: 72, height: 72 }
            )
          } else if (file.type.includes('image/')) {
            startImageUpload(file, view, editor.state.selection.from, {
              teamId,
              repoId,
            })
          } else if (SUPPORTED_LLM_FILE_TYPES.includes(file.type)) {
            startFileUpload(file, view, editor.state.selection.from, {
              teamId,
              repoId,
            })
          } else {
            return toast.error(`File type not supported.`)
          }
        }}
      />
    </Toolbar>
  )
}

export function useIsEditorEmpty(editor: Editor | null) {
  const [isEmpty, setIsEmpty] = React.useState(true) // <- default

  React.useEffect(() => {
    if (!editor) return

    // handler local
    const update = () => {
      setIsEmpty(editor.isEmpty || isReallyEmptyJson(editor.getJSON()))
    }

    editor.on('transaction', update)
    update()
    return () => {
      editor.off('transaction', update)
    }
  }, [editor])

  return isEmpty
}
